import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Activity, Building2, Handshake, FileText, ChevronDown } from "lucide-react";
import { formatDistanceToNow, isToday, isYesterday, format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface ActivityEntry {
  id: string;
  category: string;
  title: string;
  entityType: string | null;
  entityId: string | null;
  metadata: any;
  createdAt: string;
  actorId: string | null;
  actorFirstName: string | null;
  actorLastName: string | null;
  actorAvatarUrl: string | null;
}

const CATEGORY_CONFIG: Record<string, { label: string; icon: any; color: string; badgeVariant: string }> = {
  client:   { label: "Client",     icon: Building2, color: "text-blue-500",   badgeVariant: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  reseller: { label: "Revendeur",  icon: Handshake, color: "text-purple-500", badgeVariant: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300" },
  document: { label: "Document",   icon: FileText,  color: "text-green-500",  badgeVariant: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
  annonce:  { label: "Annonce",    icon: Activity,  color: "text-orange-500", badgeVariant: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300" },
};

function getCategoryConfig(category: string) {
  return CATEGORY_CONFIG[category] ?? {
    label: category,
    icon: Activity,
    color: "text-muted-foreground",
    badgeVariant: "bg-secondary text-secondary-foreground",
  };
}

function getInitials(firstName: string | null, lastName: string | null): string {
  const f = firstName?.[0] ?? "";
  const l = lastName?.[0] ?? "";
  return (f + l).toUpperCase() || "?";
}

function getDayLabel(dateStr: string): string {
  const d = new Date(dateStr);
  if (isToday(d)) return "Aujourd'hui";
  if (isYesterday(d)) return "Hier";
  return format(d, "EEEE d MMMM yyyy", { locale: fr });
}

function groupByDay(entries: ActivityEntry[]): Array<{ day: string; items: ActivityEntry[] }> {
  const map = new Map<string, ActivityEntry[]>();
  for (const entry of entries) {
    const d = new Date(entry.createdAt);
    const key = format(d, "yyyy-MM-dd");
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(entry);
  }
  return Array.from(map.entries()).map(([, items]) => ({
    day: getDayLabel(items[0].createdAt),
    items,
  }));
}

const PAGE_SIZE = 50;

export default function ActivitePage() {
  const [offset, setOffset] = useState(0);
  const [allEntries, setAllEntries] = useState<ActivityEntry[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const { isLoading, isFetching } = useQuery<{ rows: ActivityEntry[]; total: number }>({
    queryKey: ["/api/activity", offset],
    queryFn: async () => {
      const res = await authFetch(`/api/activity?limit=${PAGE_SIZE}&offset=${offset}`);
      const data = await res.json();
      setAllEntries(prev => {
        const ids = new Set(prev.map(e => e.id));
        const fresh = (data.rows ?? []).filter((r: ActivityEntry) => !ids.has(r.id));
        return offset === 0 ? data.rows ?? [] : [...prev, ...fresh];
      });
      setHasMore((data.rows?.length ?? 0) === PAGE_SIZE);
      return data;
    },
    staleTime: 30_000,
  });

  const groups = groupByDay(allEntries);

  return (
    <>
      <div className="max-w-2xl mx-auto px-4 py-8 w-full">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
            <Activity className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-foreground">Activité</h1>
            <p className="text-sm text-muted-foreground">Journal collectif de l'entreprise</p>
          </div>
        </div>

        {isLoading && offset === 0 ? (
          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex gap-3 items-start">
                <Skeleton className="w-9 h-9 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : allEntries.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
            <Activity className="w-10 h-10 text-muted-foreground/40" />
            <p className="text-muted-foreground text-sm">Aucune activité enregistrée pour l'instant.</p>
            <p className="text-muted-foreground/60 text-xs">Les créations de clients, revendeurs et documents apparaîtront ici.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {groups.map((group) => (
              <div key={group.day}>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 pl-1">
                  {group.day}
                </p>
                <div className="space-y-1">
                  {group.items.map((entry) => {
                    const cfg = getCategoryConfig(entry.category);
                    const Icon = cfg.icon;
                    const actorName = entry.actorFirstName
                      ? `${entry.actorFirstName}${entry.actorLastName ? " " + entry.actorLastName : ""}`
                      : null;
                    const timeAgo = formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true, locale: fr });
                    const isRecent = Date.now() - new Date(entry.createdAt).getTime() < 24 * 3600 * 1000;

                    return (
                      <div
                        key={entry.id}
                        data-testid={`activity-entry-${entry.id}`}
                        className={cn(
                          "flex gap-3 items-start rounded-xl px-3 py-3 transition-colors",
                          isRecent
                            ? "bg-primary/5 hover:bg-primary/8"
                            : "hover:bg-secondary/50"
                        )}
                      >
                        {entry.actorId ? (
                          <Avatar className="w-9 h-9 shrink-0 ring-2 ring-background">
                            {entry.actorAvatarUrl && (
                              <AvatarImage src={entry.actorAvatarUrl} alt={actorName ?? ""} />
                            )}
                            <AvatarFallback className="text-xs font-medium bg-primary/10 text-primary">
                              {getInitials(entry.actorFirstName, entry.actorLastName)}
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center shrink-0">
                            <Icon className={cn("w-4 h-4", cfg.color)} />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-2 flex-wrap">
                            <span className="text-sm text-foreground leading-5">{entry.title}</span>
                            <span className={cn("inline-flex items-center rounded-md px-1.5 py-0.5 text-xs font-medium shrink-0", cfg.badgeVariant)}>
                              {cfg.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {actorName && (
                              <span className="text-xs text-muted-foreground">{actorName}</span>
                            )}
                            {actorName && <span className="text-muted-foreground/40 text-xs">·</span>}
                            <span className="text-xs text-muted-foreground">{timeAgo}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {hasMore && (
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(o => o + PAGE_SIZE)}
                  disabled={isFetching}
                  data-testid="button-load-more-activity"
                  className="gap-2"
                >
                  <ChevronDown className="w-4 h-4" />
                  {isFetching ? "Chargement…" : "Charger plus"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
